import { ConditionDto } from "@babackapp/private-api-client/types";
import { useMemo } from "react";
import { FieldPath } from "react-hook-form";
import { useWorkflowForm, WorkflowsFormData } from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import { useTriggerType } from "../../hooks/use-trigger-type";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import { ARRAY_OPERATORS, type ConditionOperator, OPERATORS } from "./condition-config";


export function useConditionPanel() {
  const form = useWorkflowForm();
  const conditionsPath = useNodeFormPath("conditions");
  const triggerType = useTriggerType();
  const { conditionFields } = useWorkflowsEditorConfig();

  const availableFields = useMemo(
    () => conditionFields.filter(f => f.triggers.includes(triggerType)),
    [triggerType, conditionFields],
  );

  if (!conditionsPath) {
    return null;
  }

  const conditions = form.watch(conditionsPath) as ConditionDto[] || [];

  const addCondition = () => {
    const newCondition: ConditionDto = {
      field: "",
      operator: "equals",
      value: "",
    };
    form.setValue(conditionsPath, [...conditions, newCondition]);
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    form.setValue(conditionsPath, newConditions);
  };

  const getConditionPaths = (index: number) => {
    return {
      fieldPath: `${conditionsPath}.${index}.field` as FieldPath<WorkflowsFormData>,
      operatorPath: `${conditionsPath}.${index}.operator` as FieldPath<WorkflowsFormData>,
      valuePath: `${conditionsPath}.${index}.value` as FieldPath<WorkflowsFormData>,
    };
  };

  const isFieldAvailable = (fieldValue: string) => {
    return availableFields.some(f => f.value === fieldValue);
  };

  const handleFieldChange = (index: number, newFieldValue: string) => {
    const { operatorPath, valuePath } = getConditionPaths(index);

    const newField = conditionFields.find(f => f.value === newFieldValue);
    const currentOperator = form.getValues(operatorPath) as ConditionOperator;

    if (newField && currentOperator && !newField.operators.includes(currentOperator)) {
      const newOperator = newField.operators[0];
      form.setValue(operatorPath, newOperator as any);

      const wasArray = ARRAY_OPERATORS.includes(currentOperator);
      const isNowArray = ARRAY_OPERATORS.includes(newOperator);
      if (wasArray !== isNowArray) {
        form.setValue(valuePath, isNowArray ? [] : "" as any);
      }
    }
  };

  const handleOperatorChange = (index: number, newOperatorValue: string) => {
    const { valuePath } = getConditionPaths(index);
    const currentValue = form.getValues(valuePath);
    const wasArray = Array.isArray(currentValue);
    const isNowArray = ARRAY_OPERATORS.includes(newOperatorValue as ConditionOperator);
    if (wasArray !== isNowArray) {
      form.setValue(valuePath, isNowArray ? [] : "" as any);
    }
  };

  const getAvailableOperators = (fieldValue: string) => {
    const currentField = conditionFields.find(f => f.value === fieldValue);
    const availableOperators = currentField?.operators || [];

    return OPERATORS.filter(op => availableOperators.includes(op.value));
  };

  return {
    form,
    conditions,
    conditionsPath,
    availableFields,
    addCondition,
    removeCondition,
    getConditionPaths,
    isFieldAvailable,
    handleFieldChange,
    handleOperatorChange,
    getAvailableOperators,
  };
}
